import type { TokenStore } from '@stayon/auth-core';
import { toApiError, type Api } from '@/shared/api';
import { apiBaseUrl as API_BASE_URL } from '@/shared/api/apiBaseUrl';
import { classifyRefreshFailure } from '@/shared/api/refreshFailure';
import type { AuthState, AuthStatus } from './authState';
import { refreshCookieSession } from './cookieRefresh';
import type { IdentityGate } from './identityPublisher';
import type { RefreshCoordinator } from './refreshCoordinator';
import type { SupportSession } from './support/supportSession';

interface HydrationOptions {
  api: Api;
  tokenStore: TokenStore;
  refreshCoordinator: RefreshCoordinator;
  support: SupportSession;
  gate: IdentityGate;
  publish: (status?: AuthStatus) => void;
  setState: (state: Partial<Pick<AuthState, 'status' | 'supportResumeProblem'>>) => void;
}

/** Der Grund des Servers, warum die Support-Sitzung nicht zurückkam. */
async function resumeFailureText(error: unknown): Promise<string> {
  const { status, detail } = await toApiError(error);
  if (status === 0) return 'Der Server antwortet nicht.';
  return `Der Server antwortet mit Fehler ${status}${detail ? `: ${detail}` : '.'}`;
}

/**
 * Kaltstart: keine In-Memory-Session, nur das httpOnly-Cookie. Erst den CSRF-Token aus dem Cookie
 * bootstrappen (per JS nicht lesbar), dann via /refresh eine frische Session ziehen — direkter
 * Call ohne HTTP-Client (sonst Rekursion mit dem 401-Hook). Hatte der Tab vor dem Neuladen eine
 * Support-Sitzung, nimmt er sie danach wieder auf; bis das entschieden ist, bleibt `hold` stehen.
 */
export function createHydration({
  api,
  tokenStore,
  refreshCoordinator,
  support,
  gate,
  publish,
  setState,
}: HydrationOptions) {
  let inFlight: Promise<void> | null = null;

  /** Die Identität des Tabs steht fest; ein offener Hinweis zur Wiederaufnahme ist erledigt. */
  function settle() {
    gate.hold = null;
    setState({ supportResumeProblem: null });
    publish(tokenStore.get() ? 'authenticated' : 'unauthenticated');
  }

  async function run() {
    try {
      const { csrfToken } = await api.auth.csrf();
      await refreshCoordinator.runRefresh(() => refreshCookieSession(API_BASE_URL, csrfToken));
      if (gate.hold && tokenStore.get()) {
        await support.resume();
        settle();
      }
    } catch (error) {
      if (classifyRefreshFailure(error).definitive) {
        gate.hold = null;
        support.forget('signed-out');
        tokenStore.clear();
      } else if (gate.hold && tokenStore.get()) {
        // Die Heimatsitzung steht, nur die Support-Sitzung nicht: der Tab nennt den Grund und
        // bietet an, ohne sie weiterzuarbeiten, statt nur „Verbindung unterbrochen“ zu zeigen.
        gate.hold = 'reconnecting';
        setState({ supportResumeProblem: await resumeFailureText(error) });
        publish();
      } else {
        setState({ status: 'reconnecting' });
      }
    }
  }

  function hydrate(): Promise<void> {
    if (inFlight) return inFlight;
    if (refreshCoordinator.hasFinalLogout()) {
      support.forget('signed-out');
      tokenStore.clear();
      setState({ status: 'unauthenticated' });
      return Promise.resolve();
    }
    gate.hold = support.needsResume() ? 'refreshing' : null;
    gate.restoring = true;
    setState({ status: 'refreshing' });
    inFlight = run().finally(() => {
      gate.restoring = false;
      inFlight = null;
    });
    return inFlight;
  }

  return {
    hydrate,
    /** Gibt die nicht wiederhergestellte Sitzung im Tab auf; beim Server läuft sie bis zum Ablauf. */
    continueWithoutSupport(): void {
      support.abandonResume();
      settle();
    },
  };
}
